import React, { useState } from 'react';
import { Search, MessageSquare, Bell, CheckCircle2, Crown, Trophy, Star } from 'lucide-react';
import { WaitlistForm } from '../components/WaitlistForm';
import { useWaitlistCount } from '../hooks/useWaitlistCount';
import { WaitlistStats } from '../components/WaitlistStats';
import { WaitlistTiers } from '../components/WaitlistTiers';

function Home() {
  const [submitted, setSubmitted] = useState(false);
  const { count } = useWaitlistCount();
  const goalSignups = 1500;
  const progress = (count / goalSignups) * 100;

  const getTierInfo = (count: number) => {
    if (count < 500) return { name: 'Grundare', spots: 500 - count };
    if (count < 1000) return { name: 'Pionjär', spots: 1000 - count };
    if (count < 1500) return { name: 'Tidig användare', spots: 1500 - count };
    return { name: 'Väntelista', spots: 0 };
  };

  const { name: currentTier, spots: remainingSpots } = getTierInfo(count);

  return (
    <>
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-12 md:py-24">
        <div className="grid md:grid-cols-2 gap-8 md:gap-20 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 md:mb-8 text-gray-900 tracking-tight leading-tight">
              AI-driven bidragshjälp för innovatörer
            </h1>
            <div className="space-y-6 mb-8">
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Utlysningslabbet är en smart AI-assistent för dig som vill spara tid och maximera dina chanser att vinna bidragsfinansiering för dina idéer och projekt.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="text-green-500 mt-1 mr-3 flex-shrink-0" size={20} />
                  <span className="text-lg text-gray-700">För <strong>R&D ansvariga och chefer</strong> som vill hitta nya finansieringsmöjligheter för sina projekt</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-green-500 mt-1 mr-3 flex-shrink-0" size={20} />
                  <span className="text-lg text-gray-700">För <strong>entreprenörer och startups</strong> som söker innovationsstöd och tillväxtkapital</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-green-500 mt-1 mr-3 flex-shrink-0" size={20} />
                  <span className="text-lg text-gray-700">För <strong>forskare</strong> som söker projektfinansiering</span>
                </li>
              </ul>
            </div>
            {!submitted ? (
              <div className="space-y-4">
                <WaitlistForm onSuccess={() => setSubmitted(true)} />
              </div>
            ) : (
              <div className="bg-green-50 p-6 md:p-8 rounded-xl border border-green-100 shadow-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <CheckCircle2 className="text-green-500" size={28} />
                  <p className="text-gray-900 font-semibold text-lg">
                    Tack för ditt intresse!
                  </p>
                </div>
                <p className="text-gray-600 text-lg">
                  Vi håller dig uppdaterad om projektets utveckling.
                </p>
              </div>
            )}
          </div>
          <div className="hidden md:block">
            <img
              src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=600"
              alt="AI Analys"
              className="rounded-3xl shadow-2xl w-full"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-20 text-gray-900">Så kommer det fungera</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-12">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                <Search className="text-blue-500" size={28} />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4 text-gray-900">Hitta rätt bidrag snabbt</h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                Vår AI analyserar dina projektidéer och matchar det direkt mot relevanta utlysningar i Sverige. Du får förslag som passar just dina behov och förutsättningar.
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                <MessageSquare className="text-blue-500" size={28} />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4 text-gray-900">Skriv vinnande ansökningar</h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                Din personliga AI co-pilot guidar dig genom kraven i ansökningsprocessen med insikter från samtliga stora bidragsgivare i Sverige och träning från över 60 000 lyckade projekt för att hjälpa dig anpassa vad bidragsgivarna letar efter och hur du bäst presenterar ditt projekt.
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200 sm:col-span-2 md:col-span-1">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                <Bell className="text-blue-500" size={28} />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold mb-4 text-gray-900">Missa aldrig en möjlighet</h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                AI-assistentent kan även kontinuerligt scanna efter nya bidragsmöjligheter som matchar din profil. Du får automatiska notifieringar om relevanta utlysningar och deadlines, så du kan fokusera på att utveckla dina projekt och idéer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Early Access Benefits Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Fördelar för tidiga supportrar</h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <p className="text-xl text-gray-600">
                Just nu rekryterar vi <span className="font-semibold text-blue-600">{currentTier}</span> – våra mest exklusiva medlemmar
              </p>
              <p className="text-lg text-gray-500">
                Endast <span className="font-semibold">{remainingSpots} platser kvar</span> i denna nivå
              </p>
            </div>
          </div>
          <WaitlistTiers />
          <div className="mt-12">
            {!submitted ? (
              <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-blue-100">
                <WaitlistStats className="mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Säkra din plats och få 6 månader gratis
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  Endast {remainingSpots} platser kvar med {currentTier === 'Grundare' ? '6' : currentTier === 'Pionjär' ? '2' : '1'} månader gratis användning och livstids VIP-support
                </p>
                <WaitlistForm onSuccess={() => setSubmitted(true)} />
              </div>
            ) : (
              <div className="max-w-2xl mx-auto bg-green-50 p-8 rounded-2xl border border-green-100 shadow-lg text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <CheckCircle2 className="text-green-500" size={28} />
                  <p className="text-gray-900 font-semibold text-xl">
                    Grattis! Du är nu registrerad som {currentTier}
                  </p>
                </div>
                <p className="text-gray-600 text-lg">
                  Vi håller dig uppdaterad om projektets utveckling.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;